import {
  DndContext,
  useSensor,
  useSensors,
  MouseSensor,
  TouchSensor,
} from "@dnd-kit/core";
import React, { useEffect, useRef, useState } from "react";
import { Button } from "antd";
import { useSelector, useDispatch } from "react-redux";
import isEqual from "lodash.isequal";
import ResizeObserver from "resize-observer-polyfill";

import { Draggable } from "./Draggable";
import { Droppable } from "./Droppable";
import restaurantSchemaActions from "../../../redux/restaurantSchema/actions";
import CreateTableModal from "./CreateTableModal";
import EditTableModal from "./EditTableModal";

import "./index.css";

const { editReservationTable, getTouchsaleTablesPerRegion } =
  restaurantSchemaActions;

const RestaurantSchemeTablesForReservations = ({
  regionId,
  backgroundImage,
}) => {
  const dispatch = useDispatch();
  const [openCreateTableModal, setOpenCreateTableModal] = useState(false);
  const [openEditTableModal, setOpenEditTableModal] = useState(false);
  const [clickedTable, setClickedTable] = useState();
  const { tables } = useSelector((state) => state.restaurantSchemaReducer);

  const [tablesData, setTablesData] = useState(tables);

  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({
    width: 1300,
    height: 650,
  });

  useEffect(() => {
    // Update tables State because we use useState to modify the elements
    if (!isEqual(tablesData, tables)) {
      setTablesData(tables);
      dispatch(
        getTouchsaleTablesPerRegion({
          organizationId: parseInt(localStorage.getItem("organizationId")),
          restaurantId: parseInt(localStorage.getItem("restaurantId")),
          regionId: regionId,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tables]);

  useEffect(() => {
    const handleResize = (entries) => {
      for (let entry of entries) {
        if (entry.target === containerRef.current) {
          const { width, height } = entry.contentRect;
          setContainerSize({ width, height });
        }
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);

    const currentContainer = containerRef.current;
    if (currentContainer) {
      resizeObserver.observe(currentContainer);
    }

    return () => {
      if (currentContainer) {
        resizeObserver.unobserve(currentContainer);
      }
    };
  }, []);

  const scaleFactor = Math.min(
    containerSize.width / 1300,
    containerSize.height / 650
  );

  const elementHeight = 70 * scaleFactor;

  function handleDragEnd(event) {
    const { id } = event.active;
    const { delta } = event;

    setTablesData((tables) =>
      tables.map((table) =>
        table.touchSaleTableId === id
          ? {
              ...table,
              x: Math.max(
                0,
                Math.min(table.x + delta.x / scaleFactor, 1300 - elementHeight)
              ),
              y: Math.max(
                0,
                Math.min(table.y + delta.y / scaleFactor, 650 - elementHeight)
              ),
            }
          : table
      )
    );

    const updateTable = tablesData.filter(
      (table) => table.touchSaleTableId === id
    )[0];

    dispatch(
      editReservationTable({
        x: Math.max(
          0,
          Math.min(updateTable.x + delta.x / scaleFactor, 1300 - elementHeight)
        ),
        y: Math.max(
          0,
          Math.min(updateTable.y + delta.y / scaleFactor, 650 - elementHeight)
        ),
        direction: updateTable.direction,
        shape: updateTable.shape,
        seatsCount: updateTable.seatsCount,
        organizationId: parseInt(localStorage.getItem("organizationId")),
        touchSaleTableId: updateTable.touchSaleTableId,
        regionId: updateTable.regionId,
      })
    );
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 6,
      },
    })
  );

  const handleDoubleClick = (id) => {
    setClickedTable(
      tablesData.filter((table) => table.touchSaleTableId === id)
    );
    // Logic to open the modal or perform other actions
    setOpenEditTableModal(true);
  };

  return (
    <>
      <CreateTableModal
        openCreateTableModal={openCreateTableModal}
        setOpenCreateTableModal={setOpenCreateTableModal}
        regionId={regionId}
      />
      <EditTableModal
        openEditTableModal={openEditTableModal}
        setOpenEditTableModal={setOpenEditTableModal}
        clickedTable={clickedTable}
        setClickedTable={setClickedTable}
      />
      <div className="reservations-schema-content-container">
        <DndContext onDragEnd={handleDragEnd} sensors={sensors}>
          <Droppable ref={containerRef} backgroundImage={backgroundImage}>
            {tablesData?.map((table) => (
              <Draggable
                key={table.tableName}
                id={table.touchSaleTableId}
                tableName={table.tableName}
                position={{
                  x: table.x * scaleFactor,
                  y: table.y * scaleFactor,
                }}
                direction={table.direction}
                src={table.src}
                alt={`Draggable ${table.tableName}`}
                height={`${elementHeight}px`}
                aspectRatio={table.aspectRatio}
                onDoubleClick={handleDoubleClick}
              />
            ))}
          </Droppable>
        </DndContext>
      </div>
      <div className="reservations-schema-content-action-buttons">
        <Button onClick={() => setOpenCreateTableModal(true)}>
          Добави маса
        </Button>
      </div>
    </>
  );
};

export default RestaurantSchemeTablesForReservations;
